import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { PageMeta } from '../components/PageMeta';

const helpfulLinks = [
  { label: 'Documentation', to: '/docs', description: 'API reference, SDKs, and widget integration' },
  { label: 'Pricing', to: '/pricing', description: 'Simple, transparent plans — free to start' },
  { label: 'Guides', to: '/guides', description: 'Practical guides on collecting feedback' },
  { label: 'Changelog', to: '/changelog', description: 'See what\'s new in Pollenate' },
];

/**
 * Catch-all 404 page. Rendered by the SPA for any URL that doesn't match a
 * route. Marks itself `noindex` since Cloudflare Pages serves the SPA
 * fallback with a 200 status.
 */
export function NotFoundPage() {
  const { pathname } = useLocation();

  // The SPA fallback responds 200, so tell crawlers not to index junk URLs.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      <PageMeta
        title="Page Not Found — Pollenate"
        description="The page you're looking for doesn't exist or has moved."
      />

      <MarketingHeader />

      <main className="flex-1 flex items-center px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-7xl sm:text-8xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent mb-6">
            404
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Page not found
          </h1>
          <p className="text-lg text-slate-600 dark:text-neutral-400 mb-2">
            We couldn't find{' '}
            <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-neutral-800 text-slate-700 dark:text-neutral-300 text-base break-all">
              {pathname}
            </code>
          </p>
          <p className="text-slate-500 dark:text-neutral-500 mb-10">
            The page may have moved, or the link might be out of date.
          </p>

          <div className="flex items-center justify-center gap-3 mb-14">
            <Link
              to="/"
              className="inline-flex items-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
            >
              Back to home
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex items-center rounded-lg border border-slate-300 dark:border-neutral-700 px-5 py-2.5 text-sm font-semibold text-slate-700 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
            >
              Go to dashboard
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {helpfulLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-lg border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 hover:border-amber-500/50 dark:hover:border-amber-500/50 transition-colors"
              >
                <span className="block font-semibold text-slate-900 dark:text-white mb-1">
                  {link.label}
                </span>
                <span className="block text-sm text-slate-500 dark:text-neutral-400">
                  {link.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
